import React, { useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useGetProgramChecklistItemsQuery, useGetResearcherProgramsQuery, useUpdateResearcherChecklistItemMutation, useGetResearcherReportsForProgramQuery, useSubmitItemForReviewMutation } from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import { ChecklistItem } from "../../utils/api/endpoints/checklists/researcherChecklistApi";
import CollaborativeResults from "../../components/checklists/CollaborativeResults";
import { FiChevronRight, FiHome, FiCheck, FiX, FiMinusCircle, FiFileText, FiMessageSquare, FiList, FiGrid, FiAlertCircle, FiSearch } from "react-icons/fi";
import toast from "react-hot-toast";


// Kanban Column Component
interface KanbanColumnProps {
  title: string;
  subtitle?: string;
  count: number;
  items: ChecklistItem[];
  headerColor: string;
  textColor: string;
  badgeColor: string;
  navigate: (path: string) => void;
  runId: string | null;
  onQuickAction: (item: ChecklistItem, action: "PASS" | "FAIL" | "NA") => void;
}

// Collaborative Results Component
// CollaborativeResults is now imported from components/checklists/CollaborativeResults

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  title,
  subtitle,
  count,
  items,
  headerColor,
  textColor,
  badgeColor,
  navigate,
  runId,
  onQuickAction
}) => {
  return (
    <div className="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 shadow-sm overflow-visible flex flex-col">
      {/* Column Header */}
      <div className={`px-4 py-3 ${headerColor} flex items-center justify-between`}>
        <div className="flex flex-col">
          <span className={`font-semibold text-[13px] ${textColor}`}>{title}</span>
          {subtitle && (
            <span className={`text-[9px] font-medium opacity-70`}>{subtitle}</span>
          )}
        </div>
        <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${badgeColor}`}>
          {count}
        </span>
      </div>

      {/* Cards Container */}
      <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center py-8 text-gray-400 text-xs">
            No items
          </div>
        ) : (
          items.map((item) => (
            <div
              key={item.program_item_id}
              onClick={() => {
                const url = `/dashboard/researcher-checklists/items/${item.program_item_id}${runId ? `?runId=${runId}` : ''}`;
                navigate(url);
              }}
              className="relative bg-white border border-gray-200 rounded-lg p-3 cursor-pointer hover:shadow-md hover:border-gray-300 transition-all group hover:z-40"
            >
              {/* Category Tag & Metadata */}
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2">
                  {item.is_assigned_to_me ? (
                    <span className="bg-indigo-50 text-indigo-600 text-[8px] font-black px-1.5 py-0.5 rounded border border-indigo-100 uppercase flex items-center gap-1 shadow-sm">
                      <div className="w-1 h-1 bg-indigo-500 rounded-full animate-pulse"></div>
                      Assigned to Me
                    </span>
                  ) : !item.is_assigned ? (
                    <span className="bg-emerald-50 text-emerald-600 text-[8px] font-black px-1.5 py-0.5 rounded border border-emerald-100 uppercase flex items-center gap-1 shadow-sm">
                      <div className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse"></div>
                      Open for All
                    </span>
                  ) : (
                    <span className="bg-amber-50 text-amber-600 text-[8px] font-black px-1.5 py-0.5 rounded border border-amber-100 uppercase">
                      Assigned to Others
                    </span>
                  )}

                  {item.category && (
                    <span className="text-[9px] text-gray-500 uppercase font-black tracking-wider bg-gray-50 px-1.5 py-0.5 rounded">
                      {item.category}
                    </span>
                  )}
                </div>
                
                {item.requirement_id && (
                  <span className="bg-slate-100 text-slate-600 text-[8px] font-mono px-1.5 py-0.5 rounded border border-slate-200">
                    {item.requirement_id}
                  </span>
                )}
              </div>

              {/* Tags/Architecture */}
              {(item.is_web || item.is_api || item.framework === "ASVS") && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {item.is_web && <span className="bg-blue-50 text-blue-500 text-[8px] font-bold px-1 py-0.5 rounded border border-blue-100 uppercase">Web</span>}
                  {item.is_api && <span className="bg-purple-50 text-purple-500 text-[8px] font-bold px-1 py-0.5 rounded border border-purple-100 uppercase">API</span>}
                  {item.framework === "ASVS" && ["l1", "l2", "l3"].map(level => (item as any)[level] && (
                    <span key={level} className="bg-green-50 text-green-600 text-[8px] font-black px-1 py-0.5 rounded border border-green-200 uppercase">{level}</span>
                  ))}
                </div>
              )}

              {/* Title */}
              <h4 className="text-xs font-bold text-gray-900 leading-snug mb-1.5 group-hover:text-blue-600 transition-colors">
                {item.title}
              </h4>

              {/* Description */}
              {item.description && (
                <p className="text-[10px] text-gray-500 line-clamp-2 leading-relaxed">
                  {item.description}
                </p>
              )}

              {/* Results & Collaborative Actions */}
              <div className="mt-3 flex items-center justify-between">
                <CollaborativeResults item={item} />
                
                {(!item.all_results || item.all_results.length === 0) && (
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold shadow-sm border border-white/20"
                    style={{ 
                      backgroundColor: 
                        item.my_result?.status === "FAILED" ? '#EF4444' : 
                        item.my_result?.status === "COMPLETED" ? '#10B981' : 
                        item.my_result?.status === "NOT_APPLICABLE" ? '#94A3B8' : 
                        item.status === "REJECTED" ? '#EF4444' : '#3B82F6' 
                    }}
                  >
                    {item.category ? item.category.substring(0, 2).toUpperCase() : 'IT'}
                  </div>
                )}
              </div>

              {/* Assigned Researchers */}
              {item.is_assigned && item.assigned_researchers && item.assigned_researchers.length > 0 && (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex -space-x-2 overflow-hidden">
                    {item.assigned_researchers.map((res) => (
                      <div 
                        key={res.user_id}
                        className="inline-block h-5 w-5 rounded-full ring-2 ring-white bg-blue-100 flex items-center justify-center text-[8px] font-bold text-blue-600 border border-blue-200"
                        title={res.display_name || res.username}
                      >
                        {(res.display_name || res.username).substring(0, 2).toUpperCase()}
                      </div>
                    ))}
                  </div>
                  <span className="text-[9px] text-gray-500 font-medium">
                    Assigned to: {item.assigned_researchers.map(r => r.display_name || r.username).join(', ')}
                  </span>
                </div>
              )}

              {/* Quick Actions (Only for assigned/unassigned items in To Do) */}
              {(item.is_assigned_to_me || !item.is_assigned) && !["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.my_result?.status || "") && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2 transition-all">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickAction(item, "PASS");
                    }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-green-50 text-green-600 hover:bg-green-100 transition-colors text-[10px] font-bold border border-green-200"
                    title="Pass"
                  >
                    <FiCheck className="w-3 h-3" />
                    PASS
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickAction(item, "FAIL");
                    }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-[10px] font-bold border border-red-200"
                    title="Fail"
                  >
                    <FiX className="w-3 h-3" />
                    FAIL
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onQuickAction(item, "NA");
                    }}
                    className="flex-1 flex items-center justify-center gap-1 py-1 rounded bg-gray-50 text-gray-500 hover:bg-gray-100 transition-colors text-[10px] font-bold border border-gray-200"
                    title="N/A"
                  >
                    <FiMinusCircle className="w-3 h-3" />
                    N/A
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

// List View Item Row Component
const ListViewRow: React.FC<{
  item: ChecklistItem;
  navigate: (path: string) => void;
  runId: string | null;
  onQuickAction: (item: ChecklistItem, action: "PASS" | "FAIL" | "NA") => void;
}> = ({ item, navigate, runId, onQuickAction }) => {
  const status = item.my_result?.status || "";
  const isCompleted = ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(status);
  const isAssignedToMe = item.is_assigned_to_me || !item.is_assigned;

  return (
    <div 
      onClick={() => {
        const url = `/dashboard/researcher-checklists/items/${item.program_item_id}${runId ? `?runId=${runId}` : ''}`;
        navigate(url);
      }}
      className="relative group flex flex-col md:flex-row items-start md:items-center gap-4 p-4 bg-white border-b border-gray-100 hover:bg-blue-50/30 transition-all cursor-pointer last:rounded-b-2xl overflow-visible hover:z-40"
    >
      {/* ID & Category */}
      {/* ID & Category removed as per request */}

      {/* Title & Description */}
      <div className="flex-1 min-w-0 pr-4 break-all">
        <h4 className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors whitespace-normal leading-snug">
          {item.title}
        </h4>
        {item.description && (
          <p className="text-[11px] text-gray-500 whitespace-normal mt-1 leading-relaxed">{item.description}</p>
        )}
      </div>

      {/* Results Column */}
      <div className="w-20 shrink-0 flex items-center overflow-visible">
        <CollaborativeResults item={item} />
      </div>

      {/* Status Badge */}
      {/* Your Result Column */}
      <div className="w-28 shrink-0 flex items-center justify-center">
        {status === "COMPLETED" && (
          <span className="bg-emerald-50 text-emerald-700 text-[9px] font-black px-2 py-1 rounded-lg uppercase border border-emerald-100 flex items-center gap-1 w-full justify-center shadow-sm">
            <FiCheck className="w-3 h-3" /> PASS
          </span>
        )}
        {status === "FAILED" && (
          <span className="bg-red-50 text-red-700 text-[9px] font-black px-2 py-1 rounded-lg uppercase border border-red-100 flex items-center gap-1 w-full justify-center shadow-sm">
            <FiX className="w-3 h-3" /> FAIL
          </span>
        )}
        {status === "NOT_APPLICABLE" && (
          <span className="bg-gray-50 text-gray-700 text-[9px] font-black px-2 py-1 rounded-lg uppercase border border-gray-100 flex items-center gap-1 w-full justify-center shadow-sm">
            <FiMinusCircle className="w-3 h-3" /> N/A
          </span>
        )}
        {!isCompleted && (
          <span className="bg-slate-50 text-slate-400 text-[9px] font-black px-2 py-1 rounded-lg uppercase border border-slate-100 w-full text-center">
            Pending
          </span>
        )}
      </div>



      {/* Quick Actions */}
      <div className="flex items-center gap-1.5 shrink-0 ml-auto lg:ml-0">
        {isAssignedToMe && !isCompleted ? (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); onQuickAction(item, "PASS"); }}
              className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-100 transition-all hover:shadow-sm"
              title="Quick Pass"
            >
              <FiCheck className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onQuickAction(item, "FAIL"); }}
              className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 transition-all hover:shadow-sm"
              title="Quick Fail"
            >
              <FiX className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onQuickAction(item, "NA"); }}
              className="p-2 rounded-lg bg-gray-50 text-gray-500 hover:bg-gray-100 border border-gray-100 transition-all hover:shadow-sm"
              title="Mark N/A"
            >
              <FiMinusCircle className="w-4 h-4" />
            </button>
          </>
        ) : (
          <div className="w-24 h-8 flex items-center justify-center">
            {isCompleted ? (
               <FiChevronRight className="text-gray-300 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            ) : (
               <span className="text-[9px] font-bold text-gray-400 italic">Not Assigned</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Main List View Component
const ListView: React.FC<{
  items: ChecklistItem[];
  navigate: (path: string) => void;
  runId: string | null;
  onQuickAction: (item: ChecklistItem, action: "PASS" | "FAIL" | "NA") => void;
}> = ({ items, navigate, runId, onQuickAction }) => {
  if (items.length === 0) {
    return (
      <div className="max-w-7xl mx-auto mt-8 bg-white rounded-2xl border-2 border-dashed border-gray-200 p-20 text-center">
        <FiSearch className="mx-auto h-12 w-12 text-gray-300" />
        <h3 className="mt-4 text-lg font-bold text-gray-900">No matching test cases found</h3>
        <p className="mt-2 text-sm text-gray-500">Try adjusting your search or filters.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-visible mb-12">
      {/* List Header */}
      <div className="hidden md:flex items-center gap-4 px-6 py-4 bg-gray-50/50 border-b border-gray-200">
        <div className="flex-1 text-[10px] font-black text-gray-400 uppercase tracking-widest">Test Case Details</div>
        <div className="w-20 shrink-0 text-[10px] font-black text-gray-400 uppercase tracking-widest">Testers</div>
        <div className="w-28 shrink-0 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">Your Result</div>
        <div className="flex items-center shrink-0 w-28 justify-end text-[10px] font-black text-gray-400 uppercase tracking-widest pr-4">Actions</div>
      </div>
      
      {/* Items */}
      <div className="divide-y divide-gray-50">
        {items.map(item => (
          <ListViewRow 
            key={item.program_item_id} 
            item={item} 
            navigate={navigate} 
            runId={runId} 
            onQuickAction={onQuickAction} 
          />
        ))}
      </div>
    </div>
  );
};

const ResearcherChecklistItemsPage = () => {
  const { programId } = useParams<{ programId: string }>();
  const [searchParams] = useSearchParams();
  const runId = searchParams.get("runId");
  const navigate = useNavigate();
  const { data, isLoading } = useGetProgramChecklistItemsQuery({ 
    programId: Number(programId), 
    runId: runId ? Number(runId) : null 
  });

  const { data: programsData } = useGetResearcherProgramsQuery();
  const currentProgram = programsData?.data?.find(p => 
    p.program_id === Number(programId) && 
    (runId ? p.run_id === Number(runId) : !p.run_id)
  );

  const [updateItem, { isLoading: isUpdating }] = useUpdateResearcherChecklistItemMutation();
  const { data: reportsData } = useGetResearcherReportsForProgramQuery(
    { programId: Number(programId), runId: runId ? Number(runId) : null },
    { skip: !programId }
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("ALL");
  const [categoryFilter, setCategoryFilter] = useState<string>("ALL");
  const [assignmentFilter, setAssignmentFilter] = useState<string>("ALL");
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");

  // Modal State
  const [showActionModal, setShowActionModal] = useState(false);
  const [currentItem, setCurrentItem] = useState<ChecklistItem | null>(null);
  const [currentAction, setCurrentAction] = useState<"PASS" | "FAIL" | "NA" | null>(null);
  const [notes, setNotes] = useState("");
  const [selectedReportIds, setSelectedReportIds] = useState<number[]>([]);

  const checklists = data?.data || [];

  // Flatten all items from all checklists
  const allItems: ChecklistItem[] = checklists.flatMap((checklist) => checklist.items || []);

  // Get unique categories
  const categories = useMemo(() => {
    const cats = allItems.map(item => item.category).filter(Boolean);
    return Array.from(new Set(cats));
  }, [allItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      const term = searchTerm.trim().toLowerCase();
      // Support searching for #REQ (strip the #)
      const sanitizedTerm = term.startsWith('#') ? term.substring(1) : term;

      const titleMatch = item.title.toLowerCase().includes(term);
      const categoryMatch = item.category?.toLowerCase().includes(term);
      const idMatch = item.requirement_id?.toLowerCase().includes(sanitizedTerm);
      
      const matchesSearch = !term || titleMatch || categoryMatch || idMatch;

      const resStatus = item.my_result?.status || "PENDING";
      const matchesStatus = statusFilter === "ALL" || 
        (statusFilter === "PASS" && resStatus === "COMPLETED") ||
        (statusFilter === "FAIL" && resStatus === "FAILED") ||
        (statusFilter === "NA" && resStatus === "NOT_APPLICABLE") ||
        (statusFilter === "PENDING" && resStatus === "PENDING");

      const matchesCategory = categoryFilter === "ALL" || item.category === categoryFilter;

      const matchesAssignment = assignmentFilter === "ALL" ||
        (assignmentFilter === "ME" && item.is_assigned_to_me) ||
        (assignmentFilter === "OTHERS" && item.is_assigned && !item.is_assigned_to_me) ||
        (assignmentFilter === "OPEN" && !item.is_assigned);

      return matchesSearch && matchesStatus && matchesCategory && matchesAssignment;
    });
  }, [allItems, searchTerm, statusFilter, categoryFilter, assignmentFilter]);

  const handleQuickAction = (item: ChecklistItem, action: "PASS" | "FAIL" | "NA") => {
    setCurrentItem(item);
    setCurrentAction(action);
    setNotes("");
    setSelectedReportIds([]);
    setShowActionModal(true);
  };

  const confirmAction = async () => {
    if (!currentItem || !currentAction) return;

    if (currentAction === "FAIL" && selectedReportIds.length === 0) {
      toast.error("Please select at least one report for a failed test case.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("itemId", String(currentItem.program_item_id));
      
      let status = "";
      if (currentAction === "PASS") status = "COMPLETED";
      else if (currentAction === "FAIL") status = "FAILED";
      else if (currentAction === "NA") status = "NOT_APPLICABLE";

      formData.append("status", status);
      formData.append("notes", notes);
      
      if (currentAction === "FAIL") {
        formData.append("bugFound", "true");
        formData.append("linkedReportIds", JSON.stringify(selectedReportIds));
      }

      // Include version if available for optimistic locking
      if (currentItem.version !== undefined) {
        formData.append("version", String(currentItem.version));
      }

      await updateItem(formData).unwrap();
      
      toast.success(`Item updated to ${status.replace("_", " ")}`);
      setShowActionModal(false);
    } catch (error: any) {
      toast.error(error?.data?.message || "Failed to update item");
    }
  };

  // Filter items into columns
  const todoItems = useMemo(() => {
    return filteredItems.filter(item => 
      (item.is_assigned_to_me || !item.is_assigned) && 
      !["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.my_result?.status || "")
    );
  }, [filteredItems]);

  const othersItems = useMemo(() => {
    return filteredItems.filter(item => item.is_assigned_to_others);
  }, [filteredItems]);

  const verifiedItems = useMemo(() => {
    return filteredItems.filter(item => 
      ["COMPLETED", "FAILED", "NOT_APPLICABLE"].includes(item.my_result?.status || "")
    );
  }, [filteredItems]);

  // Calculate completed items (Matches backend calculation)
  const myResponsibleItems = useMemo(() => {
    return allItems.filter(item => item.is_assigned_to_me || !item.is_assigned);
  }, [allItems]);

  const completedItems = useMemo(() => {
    return myResponsibleItems.filter(item => 
      ["COMPLETED", "NOT_APPLICABLE", "FAILED"].includes(item.my_result?.status || "")
    ).length;
  }, [myResponsibleItems]);

  const myPassedTests = useMemo(() => {
    return myResponsibleItems.filter(item => 
      item.my_result?.status === "COMPLETED" && 
      !item.my_result?.bug_found &&
      !(item.my_result?.linked_report_ids && item.my_result?.linked_report_ids.length > 0)
    ).length;
  }, [myResponsibleItems]);

  const myFailedTests = useMemo(() => {
    return myResponsibleItems.filter(item => 
      item.my_result?.status === "FAILED" || 
      item.my_result?.bug_found === true ||
      (item.my_result?.linked_report_ids && item.my_result?.linked_report_ids.length > 0)
    ).length;
  }, [myResponsibleItems]);

  const myNaItems = useMemo(() => {
    return myResponsibleItems.filter(item => item.my_result?.status === "NOT_APPLICABLE").length;
  }, [myResponsibleItems]);

  const totalItems = myResponsibleItems.length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Get checklist name from first checklist
  const checklistName = checklists.length > 0 ? checklists[0].checklist_name : "Security Checklist";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-xl font-bold text-gray-900">Loading Checklist Items...</p>
          <p className="text-sm text-gray-600 mt-2">Please wait</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-slate-100">
      {/* Context Breadcrumb / Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm px-8 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
          <button 
            onClick={() => navigate("/dashboard/researcher-checklists")}
            className="text-gray-500 hover:text-blue-600 flex items-center gap-1.5 transition-colors font-medium"
          >
            <FiHome className="w-3.5 h-3.5" />
            Checklists
          </button>
          <FiChevronRight className="text-gray-300" />
          {currentProgram?.company_name && (
            <>
              <span className="text-blue-600 font-bold uppercase text-[10px] tracking-widest">{currentProgram.company_name}</span>
              <FiChevronRight className="text-gray-300" />
            </>
          )}
          <span className="text-gray-600 font-semibold">{currentProgram?.program_title || "Program"}</span>
          <FiChevronRight className="text-gray-300" />
          {runId ? (
            <div className="flex items-center gap-2">
              <span className="text-blue-600 font-bold bg-blue-50 px-2.5 py-0.5 rounded-lg border border-blue-100">
                Run: {currentProgram?.run_name || runId}
              </span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-900 font-bold">{checklistName}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-[#58CC02] font-bold bg-green-50 px-2.5 py-0.5 rounded-lg border border-green-100 uppercase text-[10px] tracking-widest">
                Master Checklist
              </span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-900 font-bold">{checklistName}</span>
            </div>
          )}
        </div>
      </div>

      {/* Header - Search and Progress */}
      <div className="sticky top-0 z-20 p-4">
        <div
          className="max-w-7xl mx-auto px-6 py-4 rounded-xl flex items-center justify-between shadow-xl"
          style={{ backgroundColor: '#0F172A' }}
        >
          {/* Left Side - Icon, Title, Progress */}
          <div className="flex items-center gap-4">
            {/* Back Button */}
            <button
              onClick={() => navigate("/dashboard/researcher-checklists")}
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors group"
              title="Back to programs"
            >
              <svg className="w-5 h-5 text-gray-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            {/* Progress Info */}
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-base font-bold text-white tracking-tight">{checklistName}</h1>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${runId ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' : 'bg-[#58CC02]/20 text-[#58CC02] border border-[#58CC02]/30'}`}>
                  {runId ? 'Run Checklist' : 'Product Master'}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1.5 pt-1.5 border-t border-gray-800">
                <div className="flex items-center gap-3">
                  <span className="text-[11px] text-gray-400 font-medium">
                    {completedItems} of {totalItems} items performed
                  </span>
                  <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden border border-gray-700">
                    <div
                      className="h-full rounded-full transition-all duration-500 ease-out"
                      style={{
                        width: `${progressPercent}%`,
                        backgroundColor: '#58CC02'
                      }}
                    />
                  </div>
                  <span className="text-[11px] font-bold text-[#58CC02]">{progressPercent}%</span>
                </div>

                <div className="flex items-center gap-4 pl-4 border-l border-gray-800">
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest leading-none">Passed</span>
                    <span className="text-xs font-bold text-blue-400">{myPassedTests}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest leading-none">Failed</span>
                    <span className="text-xs font-bold text-red-500">{myFailedTests}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-gray-500 uppercase tracking-widest leading-none">N/A</span>
                    <span className="text-xs font-bold text-amber-500">{myNaItems}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Search & View Switcher */}
          <div className="flex items-center gap-3">
            {/* View Switcher */}
            <div className="flex items-center bg-slate-800/80 p-1 rounded-xl border border-slate-700/50">
              <button
                onClick={() => setViewMode("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                  viewMode === "list" 
                    ? "bg-blue-600 text-white shadow-lg" 
                    : "text-gray-400 hover:text-gray-200 hover:bg-slate-700/50"
                }`}
              >
                <FiList className="w-3.5 h-3.5" />
                List
              </button>
              <button
                onClick={() => setViewMode("kanban")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                  viewMode === "kanban" 
                    ? "bg-blue-600 text-white shadow-lg" 
                    : "text-gray-400 hover:text-gray-200 hover:bg-slate-700/50"
                }`}
              >
                <FiGrid className="w-3.5 h-3.5" />
                Board
              </button>
            </div>

            <div className="relative group">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-72 pl-10 pr-4 py-2 text-sm text-gray-100 placeholder-gray-500 bg-slate-800/50 border border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#58CC02]/50 focus:border-[#58CC02]/50 transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white border-b border-gray-200 px-8 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Filters:</span>
          </div>

          {/* Assignment Filter */}
          <div className="flex items-center bg-gray-100 p-0.5 rounded-lg border border-gray-200">
            {[
              { id: "ALL", label: "All Tests" },
              { id: "ME", label: "My Tests" },
              { id: "OPEN", label: "Open" },
              { id: "OTHERS", label: "Others" }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setAssignmentFilter(f.id)}
                className={`px-3 py-1 rounded-md text-[10px] font-semibold transition-all ${
                  assignmentFilter === f.id 
                    ? "bg-white text-blue-600 shadow-sm border border-gray-200" 
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="h-4 w-px bg-gray-200 mx-1"></div>

          {/* Result Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-gray-700 text-[10px] font-semibold rounded-lg focus:ring-blue-500 focus:border-blue-500 px-2.5 py-1"
          >
            <option value="ALL">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="PASS">Passed</option>
            <option value="FAIL">Failed</option>
            <option value="NA">N/A</option>
          </select>

          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-gray-50 border border-gray-200 text-gray-700 text-[10px] font-semibold rounded-lg focus:ring-blue-500 focus:border-blue-500 px-2.5 py-1"
          >
            <option value="ALL">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>

          {/* Clear Filters */}
          {(searchTerm || statusFilter !== "ALL" || categoryFilter !== "ALL" || assignmentFilter !== "ALL") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("ALL");
                setCategoryFilter("ALL");
                setAssignmentFilter("ALL");
              }}
              className="text-[10px] font-bold text-red-500 hover:text-red-600 flex items-center gap-1 ml-auto"
            >
              <FiX className="w-3 h-3" /> Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="px-4 pb-6">
        {viewMode === "kanban" ? (
          /* Kanban Board */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {/* To Do Column */}
            <KanbanColumn
              title="To Do"
              count={todoItems.length}
              items={todoItems}
              headerColor="bg-gray-100"
              textColor="text-gray-700"
              badgeColor="bg-gray-200 text-gray-600"
              navigate={navigate}
              runId={runId}
              onQuickAction={handleQuickAction}
            />

            {/* Verified Column */}
            <KanbanColumn
              title="Verified"
              count={verifiedItems.length}
              items={verifiedItems}
              headerColor="bg-emerald-50"
              textColor="text-emerald-600"
              badgeColor="bg-emerald-100 text-emerald-500"
              navigate={navigate}
              runId={runId}
              onQuickAction={handleQuickAction}
            />

            {/* Others' Tests Column (Last) */}
            <KanbanColumn
              title="Others' Tests"
              subtitle="View-only access (assigned to others)"
              count={othersItems.length}
              items={othersItems}
              headerColor="bg-blue-50"
              textColor="text-blue-700"
              badgeColor="bg-blue-100 text-blue-600"
              navigate={navigate}
              runId={runId}
              onQuickAction={handleQuickAction}
            />
          </div>
        ) : (
          /* List View */
          <ListView 
            items={filteredItems}
            navigate={navigate}
            runId={runId}
            onQuickAction={handleQuickAction}
          />
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className={`px-6 py-4 flex items-center justify-between ${
              currentAction === 'PASS' ? 'bg-green-50' : 
              currentAction === 'FAIL' ? 'bg-red-50' : 'bg-gray-50'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${
                  currentAction === 'PASS' ? 'bg-green-100 text-green-600' : 
                  currentAction === 'FAIL' ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {currentAction === 'PASS' ? <FiCheck className="w-5 h-5" /> : 
                   currentAction === 'FAIL' ? <FiX className="w-5 h-5" /> : <FiMinusCircle className="w-5 h-5" />}
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">
                    {currentAction === 'PASS' ? 'Pass Test Case' : 
                     currentAction === 'FAIL' ? 'Fail Test Case' : 'Mark as N/A'}
                  </h3>
                  <p className="text-xs text-gray-500 truncate w-48">{currentItem?.title}</p>
                </div>
              </div>
              <button 
                onClick={() => setShowActionModal(false)}
                className="p-1 hover:bg-black/5 rounded-full transition-colors"
              >
                <FiX className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Notes Field */}
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block flex items-center gap-1.5">
                  <FiMessageSquare className="w-3 h-3" />
                  Quick Notes
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Attach quick notes to this update..."
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none min-h-[100px]"
                />
              </div>

              {/* Report Selection for FAIL */}
              {currentAction === "FAIL" && (
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5 block flex items-center gap-1.5">
                    <FiFileText className="w-3 h-3" />
                    Link Reported Vulnerabilities
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-100 rounded-xl space-y-1 p-1 bg-gray-50/50">
                    {reportsData?.data && reportsData.data.length > 0 ? (
                      reportsData.data.map((report) => (
                        <label 
                          key={report.report_id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border ${
                            selectedReportIds.includes(report.report_id) 
                              ? 'bg-blue-50 border-blue-200 shadow-sm' 
                              : 'hover:bg-white border-transparent'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedReportIds.includes(report.report_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedReportIds([...selectedReportIds, report.report_id]);
                              } else {
                                setSelectedReportIds(selectedReportIds.filter(id => id !== report.report_id));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                          />
                          <div className="min-w-0">
                            <p className="text-xs font-bold text-gray-900 truncate">#{report.report_id} - {report.report_title}</p>
                            <p className="text-[10px] text-gray-500 font-medium">{report.severity_category || 'N/A'} • {report.state || 'N/A'}</p>
                          </div>
                        </label>
                      ))
                    ) : (
                      <div className="p-4 text-center">
                        <p className="text-xs text-gray-400 italic">No reports found for this program.</p>
                        <p className="text-[10px] text-gray-500 mt-1">Please submit a report first to link it.</p>
                      </div>
                    )}
                  </div>
                  {selectedReportIds.length === 0 && (
                    <p className="text-[10px] text-red-500 mt-1.5 flex items-center gap-1">
                      <FiX className="w-2.5 h-2.5" />
                      At least one report must be linked to fail a test case
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-gray-50 flex items-center justify-end gap-3 px-6">
              <button
                onClick={() => setShowActionModal(false)}
                className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors"
                disabled={isUpdating}
              >
                Cancel
              </button>
              <button
                onClick={confirmAction}
                disabled={isUpdating || (currentAction === "FAIL" && selectedReportIds.length === 0)}
                className={`px-6 py-2 rounded-xl text-sm font-bold text-white shadow-lg transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100 ${
                  currentAction === 'PASS' ? 'bg-green-600 hover:bg-green-700 shadow-green-200' : 
                  currentAction === 'FAIL' ? 'bg-red-600 hover:bg-red-700 shadow-red-200' : 
                  'bg-gray-600 hover:bg-gray-700 shadow-gray-200'
                }`}
              >
                {isUpdating ? 'Updating...' : 'Confirm Update'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearcherChecklistItemsPage;
